
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useState } from 'react';

export default function Affiliates() {
  const [activeTab, setActiveTab] = useState('affiliates');

  const influencerPerks = [
    {
      icon: 'ri-vip-crown-line',
      title: 'Exclusive Partnerships',
      description: 'Get priority access to sponsored content opportunities and brand collaborations.',
    },
    {
      icon: 'ri-gift-line',
      title: 'Free Premium Access',
      description: 'Influencers with 10K+ followers get complimentary access to MandaPost Professional.',
    },
    {
      icon: 'ri-camera-line',
      title: 'Co-Creation Opportunities',
      description: 'Work with our team to create content, tutorials, and showcase your expertise.',
    },
    {
      icon: 'ri-trophy-line',
      title: 'Featured Spotlight',
      description: 'Get featured on our blog, social media, and marketing campaigns.',
    },
  ];

  const benefits = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Generous Commissions',
      description: 'Earn up to 30% recurring commission on every paying customer you refer.',
    },
    {
      icon: 'ri-refresh-line',
      title: 'Recurring Revenue',
      description: 'Get paid monthly as long as your referrals remain active subscribers.',
    },
    {
      icon: 'ri-dashboard-line',
      title: 'Real-Time Dashboard',
      description: 'Track your referrals, clicks, conversions, and earnings in real-time.',
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Fast Payouts',
      description: 'Receive your commissions via PayPal or bank transfer within 30 days.',
    },
    {
      icon: 'ri-service-line',
      title: 'Dedicated Support',
      description: 'Get priority support and resources to help you succeed.',
    },
    {
      icon: 'ri-star-line',
      title: 'Exclusive Resources',
      description: 'Access marketing materials, banners, and promotional content.',
    },
  ];

  const howItWorks = [
    {
      step: '1',
      title: 'Sign Up for Free',
      description: 'Join our affiliate program in minutes. No fees, no commitments.',
      icon: 'ri-user-add-line',
    },
    {
      step: '2',
      title: 'Get Your Unique Link',
      description: 'Receive your personalized referral link and promotional materials.',
      icon: 'ri-link',
    },
    {
      step: '3',
      title: 'Share & Promote',
      description: 'Share your link on social media, blogs, newsletters, or with your network.',
      icon: 'ri-megaphone-line',
    },
    {
      step: '4',
      title: 'Earn Commission',
      description: 'Get paid for every successful referral that becomes a paying customer.',
      icon: 'ri-money-dollar-box-line',
    },
  ];

  const commissionTiers = [
    {
      plan: 'Starter Plan',
      commission: '20%',
      amount: '$5.80',
      color: 'from-blue-500 to-blue-600',
    },
    {
      plan: 'Professional Plan',
      commission: '25%',
      amount: '$19.75',
      color: 'from-[#38a3a5] to-[#66b3ff]',
      popular: true,
    },
    {
      plan: 'Business Plan',
      commission: '30%',
      amount: '$59.70',
      color: 'from-purple-500 to-purple-600',
    },
    {
      plan: 'Enterprise Plan',
      commission: '30%',
      amount: 'Custom',
      color: 'from-orange-500 to-orange-600',
    },
  ];

  const referralRewards = [
    {
      referrals: '1-5',
      bonus: '$50',
      description: 'Get your first referrals',
    },
    {
      referrals: '6-10',
      bonus: '$100',
      description: 'Double your earnings',
    },
    {
      referrals: '11-25',
      bonus: '$250',
      description: 'Power affiliate status',
    },
    {
      referrals: '26+',
      bonus: '$500+',
      description: 'Elite partner rewards',
    },
  ];

  return (
    <div className="min-h-screen bg-white font-['Montserrat']">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#22577A] to-[#38a3a5] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 rounded-full text-white font-medium mb-6">
              <i className="ri-hand-coin-line mr-2"></i>
              Earn While You Share
            </div>

            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Turn Your Network Into
              <span className="block">Recurring Income</span>
            </h1>

            <p className="text-xl lg:text-2xl text-white/90 mb-10 leading-relaxed">
              Join the MandaPost Affiliate & Referral Program and earn generous commissions by recommending the world's first AI-powered social media planning platform.
            </p>

            {/* Tab Navigation */}
            <div className="flex gap-4 justify-center mb-8">
              <button
                onClick={() => setActiveTab('affiliates')}
                className={`px-8 py-4 rounded-lg font-semibold text-lg transition-all ${
                  activeTab === 'affiliates'
                    ? 'bg-white text-[#22577A] shadow-xl'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                <i className="ri-team-line mr-2"></i>
                Affiliate Program
              </button>
              <button
                onClick={() => setActiveTab('referrals')}
                className={`px-8 py-4 rounded-lg font-semibold text-lg transition-all ${
                  activeTab === 'referrals'
                    ? 'bg-white text-[#22577A] shadow-xl'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                <i className="ri-gift-line mr-2"></i>
                Referral Rewards
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#signup"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#22577A] rounded-xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all cursor-pointer"
              >
                <i className="ri-vip-crown-line"></i>
                Join Affiliate Program
              </a>
              <a
                href="#learn-more"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold text-lg hover:bg-white/10 transition-all cursor-pointer"
              >
                <i className="ri-information-line"></i>
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Influencer Marketing Section */}
      <section className="py-20 bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-[#22577A]/10 rounded-full text-[#22577A] font-medium mb-6">
              <i className="ri-user-star-line mr-2"></i>
              Influencer Marketing Program
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-4">
              Are You an Influencer?
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Partner with MandaPost and unlock exclusive perks, sponsorship opportunities, and VIP benefits designed specifically for content creators and influencers.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-2xl p-8 border-2 border-[#66b3ff]/30 hover:shadow-xl transition-all">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <i className="ri-star-smile-line text-white text-3xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#22577A] mb-2">Micro-Influencers</h3>
                  <p className="text-gray-600">1K - 10K Followers</p>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-gray-700">
                  <i className="ri-check-line text-[#66b3ff] text-xl"></i>
                  <span>20% affiliate commission on all referrals</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <i className="ri-check-line text-[#66b3ff] text-xl"></i>
                  <span>Custom tracking dashboard</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <i className="ri-check-line text-[#66b3ff] text-xl"></i>
                  <span>Marketing materials & assets</span>
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <i className="ri-check-line text-[#66b3ff] text-xl"></i>
                  <span>Early access to new features</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-2xl p-8 text-white shadow-2xl border-4 border-[#66b3ff] relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="px-4 py-1 bg-[#66b3ff] text-[#22577A] rounded-full text-sm font-bold">
                  VIP PROGRAM
                </span>
              </div>
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <i className="ri-vip-crown-line text-white text-3xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">Macro-Influencers</h3>
                  <p className="text-white/90">10K+ Followers</p>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#e4f4ff] text-xl"></i>
                  <span>30% affiliate commission + bonuses</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#e4f4ff] text-xl"></i>
                  <span>FREE Professional Plan (lifetime)</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#e4f4ff] text-xl"></i>
                  <span>Sponsored content opportunities</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#e4f4ff] text-xl"></i>
                  <span>Featured in our marketing campaigns</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#e4f4ff] text-xl"></i>
                  <span>Co-creation & collaboration projects</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-[#e4f4ff] text-xl"></i>
                  <span>Dedicated account manager</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {influencerPerks.map((perk, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-[#66b3ff] hover:shadow-lg transition-all text-center"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className={`${perk.icon} text-white text-2xl`}></i>
                </div>
                <h4 className="text-lg font-bold text-[#22577A] mb-2">{perk.title}</h4>
                <p className="text-gray-600 text-sm">{perk.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="bg-white rounded-2xl p-8 border-2 border-[#66b3ff]/30 max-w-3xl mx-auto">
              <h3 className="text-2xl font-bold text-[#22577A] mb-4">
                Ready to Partner with Us?
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Whether you're a nano, micro, or macro influencer, we have opportunities for you. Apply now and our influencer partnerships team will review your profile within 48 hours.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white rounded-xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all cursor-pointer"
              >
                <i className="ri-user-star-line"></i>
                Apply as Influencer
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white" id="learn-more">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-4">
              Why Join Our Affiliate Program?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Partner with MandaPost and enjoy industry-leading commissions and support
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/30 rounded-2xl p-8 border-2 border-[#66b3ff]/30 hover:border-[#66b3ff] hover:shadow-xl transition-all"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  <i className={`${benefit.icon} text-white text-3xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-[#22577A] mb-3">{benefit.title}</h3>
                <p className="text-gray-700 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-[#e4f4ff]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Getting started is simple. Follow these four easy steps.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-[#66b3ff] hover:shadow-xl transition-all h-full">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-full flex items-center justify-center mb-6 shadow-lg">
                    <span className="text-white font-bold text-2xl">{item.step}</span>
                  </div>
                  <div className="mb-4">
                    <i className={`${item.icon} text-4xl text-[#66b3ff]`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#22577A] mb-3">{item.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{item.description}</p>
                </div>
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-4xl text-[#66b3ff]/30"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commission Tiers */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-4">
              Commission Structure
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Earn recurring monthly commissions on all MandaPost plans
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {commissionTiers.map((tier, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl border-2 p-8 hover:shadow-2xl transition-all relative ${
                  tier.popular ? 'border-[#66b3ff] shadow-lg scale-105' : 'border-gray-200'
                }`}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="px-4 py-1 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className={`w-16 h-16 bg-gradient-to-br ${tier.color} rounded-xl flex items-center justify-center mb-6 shadow-lg`}>
                  <i className="ri-percent-line text-white text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-[#22577A] mb-2">{tier.plan}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-[#22577A]">{tier.commission}</span>
                  <span className="text-gray-600 ml-2">commission</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">You earn per month</p>
                  <p className="text-2xl font-bold text-[#38a3a5]">{tier.amount}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-[#e4f4ff]/30 to-[#b3d9ff]/30 rounded-2xl p-8 border-2 border-[#66b3ff]/30 text-center">
            <i className="ri-information-line text-4xl text-[#22577A] mb-4"></i>
            <h3 className="text-2xl font-bold text-[#22577A] mb-3">Recurring Monthly Commissions</h3>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              These are <strong>recurring commissions</strong>. You earn every month for as long as your referred customers remain active subscribers. The more referrals you make, the more passive income you generate!
            </p>
          </div>
        </div>
      </section>

      {/* Referral Rewards */}
      <section className="py-20 bg-gradient-to-br from-[#22577A] to-[#38a3a5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Bonus Referral Rewards
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Hit milestones and unlock additional bonuses on top of your commissions
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {referralRewards.map((reward, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/20 hover:bg-white/20 hover:border-white/40 transition-all text-center"
              >
                <div className="text-6xl font-bold text-white mb-4">{reward.bonus}</div>
                <div className="text-2xl font-bold text-white mb-3">{reward.referrals} Referrals</div>
                <p className="text-white/80">{reward.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-white/90 text-lg mb-6">
              Plus, get exclusive swag, early access to new features, and invitations to partner events!
            </p>
          </div>
        </div>
      </section>

      {/* CTA Sign Up Section */}
      <section className="py-20 bg-white" id="signup">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-[#e4f4ff]/30 to-[#b3d9ff]/30 rounded-2xl border-2 border-[#66b3ff]/30 p-12 shadow-xl">
            <div className="w-20 h-20 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-rocket-line text-white text-4xl"></i>
            </div>
            <h2 className="text-4xl font-bold text-[#22577A] mb-4">
              Ready to Start Earning?
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Join thousands of affiliates already earning recurring income with MandaPost. Sign up is free and takes less than 2 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white rounded-xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all cursor-pointer"
              >
                <i className="ri-user-add-line"></i>
                Apply Now
              </a>
              <a
                href="/pricing"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-[#66b3ff] text-[#22577A] rounded-xl font-semibold text-lg hover:bg-[#e4f4ff]/20 transition-all cursor-pointer"
              >
                <i className="ri-price-tag-3-line"></i>
                View Pricing Plans
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-[#e4f4ff]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#22577A] mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                question: 'How do I get paid?',
                answer: 'Commissions are paid monthly via PayPal or bank transfer. Payments are processed within 30 days after the end of each month.',
              },
              {
                question: 'Is there a minimum payout threshold?',
                answer: 'Yes, the minimum payout threshold is $50. If you don\'t reach the threshold in a given month, your earnings roll over to the next month.',
              },
              {
                question: 'How long do cookies last?',
                answer: 'Our affiliate cookies last for 90 days, giving your referrals plenty of time to make a purchase while you still get credit.',
              },
              {
                question: 'Can I promote MandaPost on social media?',
                answer: 'Absolutely! We encourage you to share your affiliate link on social media, blogs, YouTube, podcasts, and any other platforms where your audience hangs out.',
              },
              {
                question: 'Do I need to be a MandaPost customer to join?',
                answer: 'No, you don\'t need to be a customer to join the affiliate program. However, we highly recommend trying MandaPost so you can genuinely recommend it to others.',
              },
              {
                question: 'Can I refer myself?',
                answer: 'Self-referrals are not permitted. Affiliate accounts engaging in self-referral will be terminated and commissions forfeited.',
              },
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-[#66b3ff] hover:shadow-lg transition-all"
              >
                <h3 className="text-lg font-bold text-[#22577A] mb-3 flex items-start gap-2">
                  <i className="ri-question-mark text-[#66b3ff] mt-1"></i>
                  {faq.question}
                </h3>
                <p className="text-gray-700 leading-relaxed pl-7">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
